import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from './Sidebar'; // Usamos el menú universal
import { PlusCircle, Trash2, CheckCircle, AlertTriangle, Settings } from 'lucide-react';

const Criterios = () => {
  const navigate = useNavigate();
  const [criterios, setCriterios] = useState([]);
  
  // Estados del formulario
  const [nombre, setNombre] = useState('');
  const [porcentaje, setPorcentaje] = useState('');
  const [nivelSeleccionado, setNivelSeleccionado] = useState('Secundaria'); // Selector de Nivel

  useEffect(() => { cargarCriterios(); }, []);

  const cargarCriterios = async () => {
    const token = localStorage.getItem('token');
    if (!token) return navigate('/login');
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/Criterios`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (response.ok) setCriterios(await response.json());
    } catch (error) { console.error(error); }
  };

  const handleGuardar = async (e) => {
    e.preventDefault();
    
    // Filtramos solo los criterios del nivel que estamos editando para sumar su 100%
    const criteriosDelNivel = criterios.filter(c => c.nivel === nivelSeleccionado);
    const sumaActual = criteriosDelNivel.reduce((acc, curr) => acc + curr.porcentaje, 0);
    
    if (sumaActual + parseFloat(porcentaje) > 100) {
      return alert(`¡Error! La suma en ${nivelSeleccionado} no puede exceder el 100%.`);
    }

    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/Criterios`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          nombre, 
          porcentaje: parseFloat(porcentaje), 
          nivel: nivelSeleccionado, // Guardamos a qué nivel pertenece
          activo: true 
        })
      });
      if (response.ok) {
        setNombre(''); setPorcentaje('');
        cargarCriterios();
      }
    } catch (error) { console.error(error); }
  };

  const handleEliminar = async (id) => {
    const token = localStorage.getItem('token');
    await fetch(`${process.env.REACT_APP_API_URL}/Criterios/${id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
    cargarCriterios();
  };

  // Filtramos la tabla para mostrar solo los del nivel seleccionado en la pestaña
  const criteriosMostrados = criterios.filter(c => c.nivel === nivelSeleccionado);
  const totalPeso = criteriosMostrados.reduce((acc, curr) => acc + curr.porcentaje, 0);
  const estaCompleto = totalPeso === 100;

  return (
    <div className="min-h-screen bg-gray-100 flex">
      <Sidebar />

      <main className="flex-1 p-6 overflow-y-auto">
        <h1 className="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-2">
          <Settings className="text-blue-600" /> Configuración de Evaluación
        </h1>

        {/* PESTAÑAS PARA CAMBIAR DE NIVEL */}
        <div className="flex gap-2 mb-6 border-b border-slate-300">
          {['Inicial', 'Primaria', 'Secundaria'].map(nivel => (
            <button 
              key={nivel}
              onClick={() => setNivelSeleccionado(nivel)}
              className={`px-6 py-3 font-bold text-sm rounded-t-lg transition-colors ${nivelSeleccionado === nivel ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-600 hover:bg-slate-300'}`}
            >
              Nivel {nivel}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Formulario */}
          <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-100 h-fit">
            <h2 className="text-lg font-medium mb-4 flex items-center gap-2"><PlusCircle size={20}/> Nuevo Criterio ({nivelSeleccionado})</h2>
            <form onSubmit={handleGuardar} className="space-y-4">
              <div>
                <label className="block text-sm mb-1">Nombre (Ej. Tareas)</label>
                <input type="text" required value={nombre} onChange={e => setNombre(e.target.value)} className="w-full p-2 border rounded outline-none focus:ring-2 focus:ring-blue-500 bg-slate-50" />
              </div>
              <div>
                <label className="block text-sm mb-1">Peso en Porcentaje (%)</label>
                <input type="number" required min="1" max="100" value={porcentaje} onChange={e => setPorcentaje(e.target.value)} className="w-full p-2 border rounded outline-none focus:ring-2 focus:ring-blue-500 bg-slate-50" />
              </div>
              <button type="submit" disabled={estaCompleto} className="w-full bg-blue-600 text-white p-2.5 rounded-lg hover:bg-blue-700 font-medium disabled:bg-slate-400 transition-colors">
                {estaCompleto ? '100% Alcanzado' : 'Agregar Criterio'}
              </button>
            </form>
          </div>

          {/* Lista e Indicador */}
          <div className="md:col-span-2 space-y-4">
            
            <div className={`p-4 rounded-xl border flex items-center justify-between ${estaCompleto ? 'bg-green-50 border-green-200 text-green-800' : 'bg-yellow-50 border-yellow-200 text-yellow-800'}`}>
              <div className="flex items-center gap-3">
                {estaCompleto ? <CheckCircle size={24} /> : <AlertTriangle size={24} />}
                <div>
                  <h3 className="font-bold">Total Ponderado ({nivelSeleccionado}): {totalPeso}%</h3>
                  <p className="text-sm">{estaCompleto ? 'La configuración de este nivel está lista.' : `Falta ${100 - totalPeso}% para completar el 100% obligatorio.`}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
              <table className="w-full text-left">
                <thead className="bg-slate-50 border-b"><tr className="text-sm text-slate-600">
                  <th className="p-4">Criterio de Evaluación</th>
                  <th className="p-4 text-center">Peso (%)</th>
                  <th className="p-4 text-right">Acción</th>
                </tr></thead>
                <tbody>
                  {criteriosMostrados.map(c => (
                    <tr key={c.id} className="border-b">
                      <td className="p-4 font-medium text-slate-800">{c.nombre}</td>
                      <td className="p-4 text-center font-bold text-blue-600">{c.porcentaje}%</td>
                      <td className="p-4 text-right">
                        <button onClick={() => handleEliminar(c.id)} className="text-red-500 hover:bg-red-50 p-2 rounded"><Trash2 size={18}/></button>
                      </td>
                    </tr>
                  ))}
                  {criteriosMostrados.length === 0 && (
                    <tr><td colSpan="3" className="p-6 text-center text-slate-500">No hay criterios definidos para {nivelSeleccionado}.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};
export default Criterios;